import { memo } from 'react';
import { Sun, Zap, Battery } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SystemDetailsProps {
  panelsDescription?: string | null;
  panelsQuantity?: number | null;
  inverter1?: string | null;
  inverter2?: string | null;
  inverter3?: string | null;
  batteryDescription?: string | null;
  batteryQuantity?: number | null;
  compact?: boolean;
}

/**
 * Displays solar system details (panels, inverters, batteries) in a compact format.
 * Used on Kanban cards for "Waiting for Site Survey" and "Site Survey" stages.
 */
export const SystemDetails = memo(function SystemDetails({
  panelsDescription,
  panelsQuantity,
  inverter1,
  inverter2,
  inverter3,
  batteryDescription,
  batteryQuantity,
  compact = false,
}: SystemDetailsProps) {
  // Collect all non-empty inverters
  const inverters = [inverter1, inverter2, inverter3].filter(Boolean) as string[];

  // Check if there's any data to show
  const hasPanels = panelsDescription || panelsQuantity;
  const hasInverters = inverters.length > 0;
  const hasBattery = batteryDescription || batteryQuantity;

  // Nothing to display
  if (!hasPanels && !hasInverters && !hasBattery) {
    return (
      <div className={cn(
        "flex items-center gap-1.5 text-muted-foreground/60 italic mt-2",
        compact ? "text-[10px]" : "text-xs"
      )}>
        <Sun className={cn("flex-shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
        <span>No system details</span>
      </div>
    );
  }

  // Truncate long strings for display
  const truncate = (str: string, maxLen: number) => {
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen - 1) + '…';
  };

  return (
    <div className={cn("mt-2 space-y-1", compact ? "text-[10px]" : "text-xs")}>
      {/* Panels */}
      {hasPanels && (
        <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
          <Sun className={cn("flex-shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
          <span className="truncate">
            {panelsQuantity ? `${panelsQuantity} × ` : ''}
            {panelsDescription ? truncate(panelsDescription, compact ? 15 : 20) : 'Panels'}
          </span>
        </div>
      )}

      {/* Inverters */}
      {hasInverters && (
        <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
          <Zap className={cn("flex-shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
          <span className="truncate">
            {inverters.length === 1
              ? truncate(inverters[0], compact ? 18 : 24)
              : `${inverters.length} inverters`
            }
          </span>
        </div>
      )}

      {/* Battery */}
      {hasBattery && (
        <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
          <Battery className={cn("flex-shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
          <span className="truncate">
            {batteryQuantity && batteryQuantity > 1 ? `${batteryQuantity} × ` : ''}
            {batteryDescription ? truncate(batteryDescription, compact ? 15 : 20) : 'Battery'}
          </span>
        </div>
      )}
    </div>
  );
});
